import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react"
import { FormField, useForm } from "@prosolve/common-components-ui"
import styled from "styled-components"
import AccountDropdown from "../../Shared/AccountDropdown"
import ImplementationSelector from "../../Shared/ImplementationSelector"
import useSbConfig from "../../Shared/useSbConfig"
import ProductList from "./ProductList"
import ContactSelector from "./ContactSelector"

const Row = styled.div`
  display: flex;
  gap: 15px;
`

const FieldWithNotes = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

const SectionRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-bottom: 20px;
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;

  padding: 15px;
  //border: solid 1px rgb(2 128 198 / 50%);
  //border: solid 1px rgb(0 0 0 / 10%);
  border-top: solid 3px rgb(2, 128, 198);
  border-left: solid 1px rgb(0 0 0 / 10%);
  border-right: solid 1px rgb(0 0 0 / 10%);
  border-bottom: solid 1px rgb(0 0 0 / 10%);
  border-radius: 10px;
  box-shadow: 2px 2px 5px rgb(0 0 0 / 10%);
  background: rgba(0, 0, 0, 0.01);
`

const SectionHeader = styled.div`
  color: gray;

  //border-bottom: solid 1px #0280c6;
`

const Note = styled.div`
  color: gray;
  font-size: 14px;
  font-style: italic;
`

const Warning = styled.div`
  border: solid 1px #FEB800;
  border-radius: 5px;
  background: hsl(43, 100%, 95%);
  padding: 5px 20px;
  width: 100%;
`

export default forwardRef(({
  formSchema,
  onFormValueChange
}, ref) => {
  const formData = useForm(formSchema)
  const [accountId, setAccountId] = useState(formSchema.initData?.accountId)
  const [implementationId, setImplementationId] = useState(formSchema.initData?.implementationId)
  const [products, setProducts] = useState(formSchema.initData?.products)
  const [contactSearch, setContactSearch] = useState()
  const [isEmailValid, setIsEmailValid] = useState(true)
  const productListRef = useRef()
  //console.log('createorderform refresh formSchema', formSchema, ', formData', formData)
  
  const sbConfig = useSbConfig()
  // 1.0 ${sbConfig.merchantUrl}/App/Merchant/#/Orders/
  // 2.0 ${sbConfig.merchantUrl}/App/Merchant/#/order-shipment-management/orders
  const sbOrderUrl = `${sbConfig.merchantUrl}/App/Merchant/#/order-shipment-management/orders`

  const validateEmail = () => {
    const email = formData.values.contactEmail
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
    const isValid = (email?.length === 0 || emailRegex.test(email))
    setIsEmailValid(isValid)
    return isValid
  }

  useImperativeHandle(ref, () => ({
    isValid: () => {
      // Note, don't use && because that will short-circuit the evaluation if the first is true and never run the second
      return formData.isValid() & productListRef.current.isValid() & validateEmail()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [formData])

  useEffect(() => {
    formData.setValues(formSchema.initData || {})
    setAccountId(formSchema.initData?.accountId)
    setImplementationId(formSchema.initData?.implementationId)
    setProducts(formSchema.initData?.products)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formSchema.initData])

  useEffect(() => {
    const values = {
      ...formData.values,
      accountId,
      implementationId,
      products,
      contactSearch
    }
    onFormValueChange(values)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.values, accountId, implementationId, products, contactSearch])

  return (
    <SectionRoot>
      <Section>
        <SectionHeader>Account</SectionHeader>
        <Row>
          <AccountDropdown value={accountId} onChange={(val) => setAccountId(val)} />
          {/* TODO create an opportunity selector */}
          <FormField
            style={{ flex: 1, display: 'none' }}
            fieldId='opportunityId'
            type='text'
            label='Opportunity'
            formData={formData}
          />
          <ImplementationSelector accountId={accountId} value={implementationId} onChange={(val) => setImplementationId(val)} />
        </Row>
      </Section>

      <Section>
        <SectionHeader>Contact</SectionHeader>
        <Row>
          <ContactSelector accountId={accountId} onChange={setContactSearch} />
        </Row>
        <Row>
          <FormField
            style={{ flex: 1 }}
            fieldId='contactName'
            type='text'
            label='Contact Name'
            formData={formData}
          />
        </Row>
        <Row>
          <FormField
            style={{ flex: 1 }}
            fieldId='contactEmail'
            type='text'
            label='Contact Email'
            formData={formData}
          />
          <FormField
            style={{ flex: 1 }}
            fieldId='contactPhone'
            type='text'
            label='Contact Phone'
            formData={formData}
          />
        </Row>
        {!isEmailValid &&
          <Row style={{ color: 'red' }}>
            Invalid Email
          </Row>
        }
        <Row>
          <FieldWithNotes>
            <FormField
              style={{ flex: 1 }}
              fieldId='companyName'
              type='text'
              label='Company Name'
              formData={formData}
            />
            <Note>DTC Optional, B2B Recommended</Note>
          </FieldWithNotes>
        </Row>
        <Row>
          <FormField
            style={{ flex: 1 }}
            fieldId='address'
            type='text'
            label='Shipping Address'
            formData={formData}
          />
        </Row>
        <Row>
          <FormField
            style={{ flex: 1 }}
            fieldId='city'
            type='text'
            label='City'
            formData={formData}
          />
          <FormField
            style={{ flex: 1 }}
            fieldId='state'
            type='text'
            label='State'
            formData={formData}
          />
          <FormField
            style={{ flex: 1 }}
            fieldId='zip'
            type='text'
            label='Zip'
            formData={formData}
          />
        </Row>
      </Section>

      <Section>
        <SectionHeader>Shipping</SectionHeader>
        <Row>
          <FormField
            style={{ flex: 1 }}
            fieldId='desiredArrivalDate'
            type='date'
            label='Desired Arrival Date'
            formData={formData}
          />
          <FormField
            style={{ flex: 1 }}
            fieldId='earliestArrivalDate'
            type='date'
            label='Earliest Arrival Date'
            formData={formData}
          />
          <FormField
            style={{ flex: 1 }}
            fieldId='requestedDate'
            type='date'
            label='Date Requested'
            formData={formData}
          />
        </Row>
        <Row>
          <FormField
            style={{ flex: 1 }}
            fieldId='requester'
            type='text'
            label='Requester'
            formData={formData}
          />
          <FormField
            style={{ flex: 1 }}
            fieldId='shippingInstructions'
            type='text'
            label='Special Shipping Instructions'
            formData={formData}
          />
        </Row>
        <Row>
          <FieldWithNotes>
            <FormField
              fieldId='shippingType'
              options={[ 
                { text: '', value: "" },
                { text: 'Direct to Customer (DTC)', value: "DTC" },
                { text: 'Business to Business (B2B)', value: "B2B" },
              ]}
              width='100%'
              type='select'
              label='Shipment Type'
              formData={formData}
            />
            <Note>
              B2B +Freight is recommended for orders of 25 products or more
            </Note>
          </FieldWithNotes>
          <FieldWithNotes>
            <FormField
              fieldId='carrierType'
              options={[ 
                { text: '', value: "" },
                { text: 'Parcel (One or more items)', value: "Parcel" },
                { text: 'Freight (One or more pallets)', value: "Freight" },
              ]}
              width='100%'
              type='select'
              label='Carrier Type'
              formData={formData}
            />
            <Note>DTC Ignored, B2B Required</Note>
          </FieldWithNotes>
        </Row>
      </Section>

      <Section>
        <SectionHeader>Products</SectionHeader>
        {products?.reduce((prev,curr) => prev + parseInt(curr.quantity), 0) >= 25 &&
          <Row>
            <Warning>
              <div>You are ordering more than 25 items.</div>
              <div>
                For physical items, you may wish to use B2B Shipping or complete the order using 
                the <a href={sbOrderUrl} target='_blank' rel='noreferrer'>shipbob interface</a> instead.
              </div>
            </Warning>
          </Row>
        }
        <Row>
          <ProductList ref={productListRef} products={products} onChange={setProducts} />
        </Row>
      </Section>
    </SectionRoot>
  )

})
